import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import styles from './index.module.scss';
import gtm from '@/utils/gtm';

interface TabItemProps {
    tabValue: string;
    tabLabel: React.ReactNode;
    className?: string;
}

interface TabsProps {
    tabs: TabItemProps[];
    className?: string;
    activeTab?: string;
    activeIndex: number;
    onChange: (tab: TabItemProps, index: number) => void;
}

const Tabs: React.FC<TabsProps> = ({ tabs, className, activeTab, activeIndex, onChange }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const scrollItemRef = useRef<HTMLDivElement[]>([]);

    function tabOnChange(e: React.MouseEvent<HTMLDivElement>, tab: TabItemProps, index: number) {
        onChange(tab, index);
        scrollToView(index);
        gtm.push({
            event: 'uaEvent',
            event_name: 'tab_click',
            list_name: 'none',
            button_name: tab,
            __btn_id1: `${styles.Tabs} .tab-${index + 1}`,
            __btn_id2: tab.tabLabel,
            __btn_type: 'text',
        });
    }

    useEffect(() => {
        scrollToView(activeIndex);
    }, [activeIndex]);

    function scrollToView(index: number) {
        if (!scrollRef.current) return;

        const container = scrollRef.current;
        const parentRect = container.getBoundingClientRect();
        const elemRect = scrollItemRef.current[index].getBoundingClientRect();

        const scrollLeft = elemRect.left - parentRect.left - parentRect.width / 2 + elemRect.width / 2;

        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        const minScrollLeft = 0;

        const targetScrollLeft = container.scrollLeft + scrollLeft;

        const clampedScrollLeft = Math.max(minScrollLeft, Math.min(maxScrollLeft, targetScrollLeft));

        const actualScrollDistance = clampedScrollLeft - container.scrollLeft;

        container.scrollBy({
            left: actualScrollDistance,
            behavior: 'smooth',
        });
    }

    return (
        <div ref={scrollRef} className={classNames(styles.Tabs, className)}>
            <div className="tab-box">
                {tabs.map((tab, index) => (
                    <div
                        ref={(node: any) => (scrollItemRef.current[index] = node)}
                        className={classNames(
                            'tab-item',
                            'GtmModuleData-btn-text',
                            `tab-${index + 1}`,
                            tab.tabValue === activeTab || index === activeIndex ? 'tab-item__active' : '',
                        )}
                        key={tab.tabValue}
                        onClick={(e) => tabOnChange(e, tab, index)}
                    >
                        {tab.tabLabel}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Tabs;
